import React, { Fragment, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';

import { Create, Close, RestartAlt } from '@mui/icons-material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FilterAccordian from '../filterAccordian';
import Breadcrumb from '../common/breadcrumb';
import { userRequest } from '../../auth/requestMethods';
import DeliveryDining from '@mui/icons-material/DeliveryDining';
import CustomTable from '../tables/MuiDataGrid';
import { Lock } from '@mui/icons-material';

const Manage = ({ history }) => {
  useEffect(() => {
    document.title = 'PFSA | Delivery Runs | Manage';
  }, []);
  const [perPage, setPerPage] = React.useState(10);
  const [page, setPage] = React.useState(1);
  //const [statusFilter, setStatusFilter] = React.useState(true);
  const statusFilter = true;
  const [searchInput, setSearchInput] = React.useState({
    is_finalised: 'All',
    is_active: 'All',
  });
  const [dataIsFetching, setDataIsFetching] = React.useState(true);
  const [finaliseId, setFinaliseId] = React.useState('');
  const [tableData, setTableData] = React.useState({
    count: 0,
    deliveryRuns: [],
  });

  React.useEffect(() => {
    setDataIsFetching((current) => {
      if (!current) return true;
    });

    const queryString = buildQuery();

    userRequest()
      .get(`/delivery-runs?per_page=${perPage}&page=${page}&${queryString}`)
      .then(({ data }) => {
        setTableData({
          count: data.total,
          deliveryRuns: data.deliveryRuns,
        });
        setDataIsFetching(false);
      })
      .catch(({ response }) => {
        console.log(response);
        setDataIsFetching(false);
      });
  }, [statusFilter, perPage, page, searchInput]);

  function setStatus(id, data) {
    userRequest()
      .post(`/delivery-runs/update/${id}`, data)
      .then(() => {
        let drs = [];
        if (meetFilterConditions(data)) {
          drs = tableData.deliveryRuns.map((dr) => {
            if (id === dr.id) dr.is_active = !dr.is_active;
            return dr;
          });
        } else {
          drs = tableData.deliveryRuns.filter((dr) => dr.id !== id);
        }

        setTableData({
          count: drs.length,
          deliveryRuns: drs,
        });
        // Refetch Data from API now that status has changed?
        getData();
      })
      .catch(() => {});
  }

  function meetFilterConditions(isUpdate = true) {
    if (!isUpdate) {
      if (tableData.count === perPage) return false;
    }

    return true;
  }

  const onChangeInput = ({ target: t }) => {
    setSearchInput({ ...searchInput, [t.name]: t.value });
    //getData();
  };

  useEffect(() => {
    getData();
  }, [searchInput.is_finalised, searchInput.is_active]);

  function getFilters() {
    return Object.entries(searchInput)
      .map(([key, value]) => {
        if (value !== 'All') {
          if (key === 'is_finalised') {
            return `Progress Status: ${value ? 'Finalised' : 'Draft'}`;
          }
          if (key === 'is_active') {
            return `Status: ${value ? 'Active' : 'Inactive'}`;
          }
        }
        return undefined;
      })
      .filter((x) => x !== undefined);
  }
  //  `/delivery-runs/update/${row.id}?calculate=true`
  //  `/delivery-runs/update/${row.id},{is_finalised:true}`
  // finalised_by_user_id	finalised_by_datetime
  function finalise() {
    userRequest()
      .post(`/delivery-runs/update/${finaliseId}`, { is_finalised: true })
      .then(() => {
        setTableData((pr) => ({
          ...pr,
          deliveryRuns: pr.deliveryRuns.map((dr) => {
            if (dr.id === finaliseId) dr.is_finalised = true;
            return dr;
          }),
        }));
        setFinaliseId('');
      })
      .catch((res) => console.log(res));
  }

  const actionMethods = {
    width: 140,
    custom({ row }) {
      const empty = (
        <IconButton style={{ visibility: 'hidden' }} disabled>
          <Lock />
        </IconButton>
      );

      const finalize =
        row.is_finalised || !row.is_active ? (
          empty
        ) : (
          <IconButton onClick={() => setFinaliseId(row.id)}>
            <Tooltip title="finalize" placement="top">
              <Lock />
            </Tooltip>
          </IconButton>
        );

      const editView = (
        <IconButton onClick={() => window.open('/delivery-runs/edit/' + row.id, '_blank').focus()}>
          <Tooltip title={'Edit'} placement="top">
            {row.is_finalised ? (
              <VisibilityIcon style={{ color: '#0E4BD9' }} />
            ) : (
              <Create style={{ color: '#0E4BD9' }} />
            )}
          </Tooltip>
        </IconButton>
      );

      const activateDeactivate = row.is_finalised ? (
        empty
      ) : (
        <IconButton onClick={() => setStatus(row.id, { is_active: !row.is_active })}>
          <Tooltip title={row.is_active ? 'Deactivate' : 'Activate'} placement="top">
            {row.is_active ? <Close style={{ color: '#bc3c51' }} /> : <RestartAlt style={{ color: '#28a745' }} />}
          </Tooltip>
        </IconButton>
      );

      return (
        <>
          {finalize}
          {editView}
          {activateDeactivate}
        </>
      );
    },
    //toggleEdit: (row) => window.open('/delivery-runs/edit/' + row.id, '_blank').focus(),
    //toggleActive: (row) => setStatus(row.id, { is_active: !row.is_active }),
  };

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      flex: 1,
    },
    {
      field: 'consumption_start_date',
      headerName: 'Period From',
      flex: 1,
      valueFormatter: ({ value }) => new Date(value).toISOString().slice(0, 10),
    },
    {
      field: 'consumption_end_date',
      headerName: 'Period To',
      flex: 1,
      valueFormatter: ({ value }) => new Date(value).toISOString().slice(0, 10),
    },
    {
      field: 'planned_delivery_start_date',
      headerName: 'Delivery From',
      flex: 1,
      valueFormatter: ({ value }) => new Date(value).toISOString().slice(0, 10),
    },
    {
      field: 'planned_delivery_end_date',
      headerName: 'Delivery To',
      valueFormatter: ({ value }) => new Date(value).toISOString().slice(0, 10),
      flex: 1,
    },
    {
      field: 'schools',
      headerName: 'Schools',
      flex: 1,
    },
    {
      field: 'detail',
      headerName: 'Detail',
      flex: 1,
      renderCell: ({ row }) => (
        <a href={`/reports/delivery-run-details/${row.id}`} target="_blank" rel="noopener noreferrer">
          View
        </a>
      ),
    },
    {
      field: 'is_finalised',
      headerName: 'Status',
      width: 110,
      valueFormatter: ({ value }) => (value ? 'Finalised' : 'Draft'),
    },
    {
      field: 'is_active',
      headerName: 'Active Status',
      width: 110,
      renderCell: ({ row }) =>
        row.is_active ? (
          <Typography className="font-success mx-auto">Active</Typography>
        ) : (
          <Typography className="font-danger mx-auto">Inactive</Typography>
        ),
    },
  ];

  /**
   * Returns a query string based on selected filters
   * @param {*} foundFilter
   * @returns
   */
  function buildQuery(foundFilter = null) {
    return Object.entries(searchInput)
      .map(([key, value]) => {
        if (value !== '') {
          if (foundFilter) foundFilter.push(key);

          if (key === 'is_active' || key === 'is_finalised') {
            // Handle 'All' case separately
            if (value === 'All') {
              return '';
            }
          }

          return encodeURIComponent(key) + '=' + encodeURIComponent(value);
        }
        return '';
      })
      .filter((Q) => !!Q)
      .join('&');
  }

  /**
   * Runs on form submit for searching for provided values
   */
  // function onSearch(e) {
  //   e.preventDefault();

  //   getData();
  // }

  /**
   * Function to make an API request based on selected filter options
   */
  function getData() {
    const queryString = buildQuery();

    userRequest()
      .get(`/delivery-runs?per_page=${perPage}&page=${page}&${queryString}`)
      .then(({ data }) => {
        setTableData({
          count: data.total,
          deliveryRuns: data.deliveryRuns,
        });
        setDataIsFetching(false);
      })
      .catch(({ response }) => {
        console.log(response);
        setDataIsFetching(false);
      });
  }

  return (
    <Fragment>
      <Breadcrumb icon={<DeliveryDining />} title="Manage" parent="Delivery Runs" />

      <div className="container-fluid">
        <FilterAccordian filters={getFilters()}>
          <form className="theme-form">
            <div className="form-group row mx-0">
              <FormControl>
                <InputLabel id="demo-simple-select-helper-label">Status</InputLabel>
                <Select
                  name="is_finalised"
                  value={searchInput.is_finalised}
                  onChange={onChangeInput}
                  labelId="demo-simple-select-helper-label"
                  id="demo-simple-select-helper"
                  label="Status"
                  style={{ fontStyle: searchInput.is_finalised === 'All' ? 'italic' : 'normal' }}
                >
                  <MenuItem value={'All'} style={{ fontStyle: 'italic' }}>
                    All
                  </MenuItem>
                  <MenuItem value={false}>Draft</MenuItem>
                  <MenuItem value={true}>Finalised</MenuItem>
                </Select>
              </FormControl>
            </div>

            <div className="form-group row mx-0">
              <FormControl>
                <InputLabel id="demo-simple-select-helper-label">Active Status</InputLabel>
                <Select
                  name="is_active"
                  value={searchInput.is_active}
                  onChange={onChangeInput}
                  labelId="demo-simple-select-helper-label"
                  id="demo-simple-select-helper"
                  label="Active Status"
                  style={{ fontStyle: searchInput.is_active === 'All' ? 'italic' : 'normal' }}
                >
                  <MenuItem value={'All'} style={{ fontStyle: 'italic' }}>
                    All
                  </MenuItem>
                  <MenuItem value={true}>Active</MenuItem>
                  <MenuItem value={false}>Inactive</MenuItem>
                </Select>
              </FormControl>
            </div>
            {/* <div>
              <button className="btn btn-primary me-1" type="submit">
                Search
              </button>
            </div> */}
          </form>
        </FilterAccordian>
      </div>
      <button onClick={() => history.push('/delivery-runs/create')} className="btn btn-secondary m-15" type="button">
        <i className="fa fa-plus fa-xl" aria-hidden="true"></i>
      </button>
      <div className="container-fluid">
        <CustomTable
          rows={tableData.deliveryRuns}
          actions={actionMethods}
          columns={columns}
          setPageSize={setPerPage}
          setPage={setPage}
          rowCount={tableData.count}
          pageSize={perPage}
          loading={dataIsFetching}
          sortModel={[{ field: 'consumption_start_date', sort: 'asc' }]}
        />
      </div>
      <Dialog maxWidth="md" TransitionProps={{ onEntered: () => {} }} open={!!finaliseId}>
        <DialogTitle>Finalise Deliver Run</DialogTitle>
        <DialogContent dividers>
          {`This will permanently lock the Delivery Run, so no changes can be made to it - this action cannot be undone..  Are you sure?`}
        </DialogContent>
        <DialogActions>
          <Button onClick={finalise}>Finalise</Button>
          <Button
            onClick={() => {
              setFinaliseId('');
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default Manage;
